import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export type CorrectionType = "in_only" | "out_only" | "both";

/** How the corrected times were chosen. `half_day` / `full_day` are resolved
 *  server-side from the Technical-Admin-configured defaults and always land as
 *  `correctionType: "both"`; `manual` means the filer typed them, which is the
 *  pre-existing behaviour and therefore the default. */
export type CorrectionMode = "half_day" | "full_day" | "manual";

export type RegularisationStatus = "pending" | "approved" | "rejected" | "cancelled";

export type RegularisationReasonType =
  | "whatsapp_location"
  | "forgot_mark_in"
  | "forgot_mark_out"
  | "forgot_attendance"
  | "system_issue"
  | "client_visit"
  | "official_travel"
  | "others";

export interface RegularisationRow {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  attendanceId: string | null;
  attendanceDate: string | null;
  correctionType: CorrectionType;
  correctionMode: CorrectionMode;
  correctedInTime: string | null;
  correctedOutTime: string | null;
  inLocationLabel: string;
  outLocationLabel: string;
  reasonType: RegularisationReasonType;
  reasonDetails: string;
  attachmentId: string | null;
  status: RegularisationStatus;
  submittedAt: string | null;
  submittedById: string | null;
  submittedByName: string | null;
  isProxy: boolean;
  // Direct-apply rows are also `isProxy` (submittedBy !== user), so this is
  // the only reliable way to tell an unreviewed direct-apply apart from a
  // proxy row that its filer later approved.
  isDirect: boolean;
  bypassReason: string;
  reviewedAt: string | null;
  reviewedById: string | null;
  reviewedByName: string | null;
  reviewNote: string;
  cancelReason: string;
  createdAt: string | null;
}

/** A user the actor may file/apply a regularisation for. Pool is tier-derived
 *  for direct-apply and role-fixed for proxy filing — the server decides. */
export interface RegularisationTarget {
  id: string;
  name: string;
  email: string;
  role: string;
}

/** @deprecated Renamed to RegularisationTarget once direct-apply started
 *  sharing the same picker. Kept as an alias so existing imports keep working. */
export type ProxyTarget = RegularisationTarget;

export interface RegularisationListResponse {
  results: RegularisationRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface RegularisationCreateInput {
  attendanceDate: string;
  correctionType: CorrectionType;
  correctedInTime?: string | null;
  correctedOutTime?: string | null;
  inLocationLabel?: string;
  outLocationLabel?: string;
  reasonType: RegularisationReasonType;
  reasonDetails: string;
  attachmentId?: string | null;
  // Proxy filing (Technical Admin only). Setting ``targetUserId`` routes the
  // request to ``submit_proxy_regularisation`` server-side, bypassing the
  // 3-day window.
  targetUserId?: string | null;
  bypassReason?: string;
}

/** Direct-apply input. `targetUserId` is mandatory — self-targeting is blocked
 *  server-side, so there is no "apply to me" shape. Attachment, reason details
 *  and bypass reason are all optional, unlike proxy filing: the actor already
 *  holds the write and the row is audited either way. */
export interface RegularisationDirectInput {
  targetUserId: string;
  attendanceDate: string;
  /** Omit for the pre-existing manual behaviour. With `half_day` / `full_day`
   *  the server fills the times from config and `correctionType` is ignored. */
  correctionMode?: CorrectionMode;
  correctionType?: CorrectionType | null;
  correctedInTime?: string | null;
  correctedOutTime?: string | null;
  inLocationLabel?: string;
  outLocationLabel?: string;
  reasonType: RegularisationReasonType;
  reasonDetails?: string;
  attachmentId?: string | null;
  /** Optional even past the 3-day window — SA / TA are notified regardless. */
  bypassReason?: string;
}

const listKey = (params: object) => ["regularisations", "list", params] as const;

export function useRegularisations(
  params: {
    scope?: "self" | "all" | "submitted_by_self";
    status?: RegularisationStatus | "";
    userId?: string;
    from?: string;
    to?: string;
  } = {},
  enabled: boolean = true,
) {
  return useQuery<RegularisationListResponse>({
    queryKey: listKey(params),
    queryFn: () =>
      api
        .get<RegularisationListResponse>("/attendance-regularisations", {
          params: {
            scope: params.scope || "self",
            status: params.status || undefined,
            userId: params.userId || undefined,
            from: params.from || undefined,
            to: params.to || undefined,
          },
        })
        .then((r) => r.data),
    enabled,
  });
}

export function useSubmitRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegularisationCreateInput) =>
      api
        .post<RegularisationRow>("/attendance-regularisations", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisations"] }),
  });
}

/** Applies the correction immediately — no Pending stage, no review.
 *  SA / TA / HR only (`regularisations.direct_apply`). */
export function useDirectRegularise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegularisationDirectInput) =>
      api
        .post<RegularisationRow>("/attendance-regularisations/direct", input)
        .then((r) => r.data),
    onSuccess: () => {
      // Attendance and leaves are both rewritten in the same transaction.
      qc.invalidateQueries({ queryKey: ["regularisations"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}

export function useRegularisationTargets(search: string, enabled: boolean = true) {
  return useQuery<{ results: RegularisationTarget[] }>({
    queryKey: ["regularisations", "targets", search],
    queryFn: () =>
      api
        .get<{ results: RegularisationTarget[] }>(
          "/attendance-regularisations/targets",
          { params: { search: search || undefined } },
        )
        .then((r) => r.data),
    enabled,
  });
}

export function useCancelRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api
        .post<RegularisationRow>(`/attendance-regularisations/${id}/cancel`, {
          reason: reason ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisations"] }),
  });
}

export function useReviewRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: "approved" | "rejected";
      note?: string;
    }) =>
      api
        .post<RegularisationRow>(`/attendance-regularisations/${id}/review`, {
          decision,
          note: note ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["regularisations"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Correction defaults — the times behind the Half Day / Full Day modes.
//
// One system-wide configuration (ADR-053), so these are singleton endpoints
// with no id in the path: GET / POST / PATCH `/regularisation-defaults`.
// Managed by Technical Admin (`regularisations.defaults_manage`). The `active`
// read is open to anyone who may file a regularisation, which is what lets the
// drawer show the resolved times before applying.
// ---------------------------------------------------------------------------

export interface RegularisationCorrectionDefaultsRow {
  id: string;
  label: string;
  /** "HH:MM:SS" */
  halfDayInTime: string | null;
  halfDayOutTime: string | null;
  fullDayInTime: string | null;
  fullDayOutTime: string | null;
  note: string;
  status: "active" | "inactive";
  createdAt: string | null;
  updatedAt: string | null;
}

/** `configured: false` is the pre-first-save state, not an error — it is what
 *  the panel renders its "Configure Default Times" action for. */
export interface CorrectionDefaultsConfig {
  configured: boolean;
  config: RegularisationCorrectionDefaultsRow | null;
}

/** Resolved active config. `configured: false` means no active default exists —
 *  the Half Day / Full Day modes then refuse to apply. There is deliberately no
 *  hardcoded fallback on either side of the wire. */
export interface ActiveCorrectionDefaults {
  configured: boolean;
  id: string | null;
  label: string;
  halfDayInTime: string | null;
  halfDayOutTime: string | null;
  fullDayInTime: string | null;
  fullDayOutTime: string | null;
}

export interface CorrectionDefaultsWritePayload {
  label: string;
  halfDayInTime: string;
  halfDayOutTime: string;
  fullDayInTime: string;
  fullDayOutTime: string;
  note?: string;
}

const defaultsKey = ["regularisations", "defaults"] as const;

export function useActiveCorrectionDefaults(enabled: boolean = true) {
  return useQuery<ActiveCorrectionDefaults>({
    queryKey: [...defaultsKey, "active"],
    queryFn: () =>
      api
        .get<ActiveCorrectionDefaults>("/regularisation-defaults/active")
        .then((r) => r.data),
    enabled,
  });
}

/** The single configuration, for the Technical Admin management panel. */
export function useCorrectionDefaults() {
  return useQuery<CorrectionDefaultsConfig>({
    queryKey: [...defaultsKey, "config"],
    queryFn: () =>
      api.get<CorrectionDefaultsConfig>("/regularisation-defaults").then((r) => r.data),
  });
}

/** First-time write. The server answers 409
 *  (`REGULARISATION_DEFAULTS_ALREADY_CONFIGURED`) once it exists — editing is
 *  `useUpdateCorrectionDefaults`. */
export function useCreateCorrectionDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CorrectionDefaultsWritePayload) =>
      api
        .post<RegularisationCorrectionDefaultsRow>("/regularisation-defaults", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: defaultsKey }),
  });
}

export function useUpdateCorrectionDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CorrectionDefaultsWritePayload>) =>
      api
        .patch<RegularisationCorrectionDefaultsRow>("/regularisation-defaults", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: defaultsKey }),
  });
}

export function useDeactivateCorrectionDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reason }: { reason?: string } = {}) =>
      api
        .post<RegularisationCorrectionDefaultsRow>("/regularisation-defaults/deactivate", {
          reason: reason ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: defaultsKey }),
  });
}

export function useActivateCorrectionDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api
        .post<RegularisationCorrectionDefaultsRow>("/regularisation-defaults/activate", {})
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: defaultsKey }),
  });
}

/** "09:30:00" -> "09:30" for `<input type="time">`, which rejects a seconds
 *  component in some browsers. Empty string for null, so the field stays blank
 *  rather than rendering an invalid value. */
export function toTimeInputValue(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw.slice(0, 5);
}

/** "09:30" -> "09:30:00" for the wire, which expects seconds. */
export function toTimeWireValue(raw: string): string {
  if (!raw) return "";
  return raw.length === 5 ? `${raw}:00` : raw;
}
